<script lang="ts">
  /**
   * The Webhooks tab — set up an address, point triggers at it, watch what it did.
   *
   * THE SETUP FLOW IS THREE STEPS, in this order, and the order is the point: add the
   * address, copy the signing secret (the only time it is ever shown), then wire triggers
   * to it. A webhook that fires nothing is the safe thing to have just created, so
   * nothing here starts traffic until a trigger is explicitly turned on.
   */
  import { isCodedError } from '@api/errors';
  import { QUAD, ROLE_ORDER_ALL, type Quadrant } from '@ui/quadrant';
  import StateBlock from '@ui/StateBlock.svelte';
  import { timeAgo } from '@ui/member';
  import {
    fetchWebhooks, createWebhook, deleteWebhook, setWebhookEnabled, testWebhook,
    fetchDeliveries, addTrigger, setTriggerEnabled, deleteTrigger,
    roleCondition, ROLE_CHANGED_EVENT, deliveryHealth, deliveryReason, endpointCopy,
    ageWords, aiPrompt,
    type Webhook, type EventOption, type Delivery, type TestResult,
    type SignatureInfo,
  } from '../../webhooks';

  let { skoolId, onflash }: {
    skoolId: number;
    onflash: (text: string, kind?: 'info' | 'error') => void;
  } = $props();

  let loading = $state(true);
  let error = $state<string | null>(null);
  let items = $state<Webhook[]>([]);
  let events = $state<EventOption[]>([]);
  // The body shape and signing scheme, served with the list so the reference below can
  // never describe a payload we have stopped sending.
  let sample = $state<unknown>(null);
  let signature = $state<SignatureInfo | null>(null);
  let busy = $state<string | null>(null);

  // The create form, and the secret it returns. `secret` is held in component state and
  // nowhere else — there is no route that can fetch it again, so navigating away is
  // genuinely the last chance to copy it. Said plainly in the markup rather than implied.
  let form = $state<{ name: string; url: string } | null>(null);
  let secret = $state<{ key: string; value: string } | null>(null);

  // Per-webhook "add a trigger" drafts, keyed by webhook. A record rather than one
  // draft, so opening a second webhook's picker does not discard the first.
  let drafts = $state<Record<string, { event: string; roles: string[] }>>({});
  let deliveries = $state<Record<string, Delivery[]>>({});
  let lastTest = $state<Record<string, TestResult>>({});

  const canSave = $derived(
    !!form && form.name.trim().length > 0 && form.url.trim().startsWith('https://'),
  );

  async function load(): Promise<void> {
    loading = true;
    try {
      const data = await fetchWebhooks(skoolId);
      items = data.items;
      events = data.events;
      sample = data.sample;
      signature = data.signature;
      error = null;
    } catch (e) {
      error = e instanceof Error ? e.message : 'Failed to load webhooks.';
    } finally {
      loading = false;
    }
  }

  $effect(() => { void skoolId; void load(); });

  async function save(): Promise<void> {
    if (!form || !canSave) return;
    busy = 'create';
    try {
      const made = await createWebhook(skoolId, { name: form.name.trim(), url: form.url.trim() });
      secret = { key: made.key, value: made.secret };
      form = null;
      await load();
      onflash('Webhook added — copy the signing secret now.');
    } catch (e) {
      onflash(e instanceof Error ? e.message : 'Could not add that webhook.', 'error');
    } finally {
      busy = null;
    }
  }

  async function toggle(w: Webhook): Promise<void> {
    busy = w.key;
    try {
      await setWebhookEnabled(skoolId, w.key, !w.enabled);
      await load();
    } catch (e) {
      onflash(e instanceof Error ? e.message : 'Could not change that.', 'error');
    } finally {
      busy = null;
    }
  }

  async function remove(w: Webhook): Promise<void> {
    const n = w.triggers.length;
    const warning = n > 0
      ? `Delete “${w.name}”? Its ${n} trigger${n === 1 ? '' : 's'} go with it.`
      : `Delete “${w.name}”?`;
    if (!confirm(warning)) return;
    busy = w.key;
    try {
      await deleteWebhook(skoolId, w.key);
      if (secret?.key === w.key) secret = null;
      await load();
      onflash('Webhook deleted.');
    } catch (e) {
      onflash(e instanceof Error ? e.message : 'Could not delete that.', 'error');
    } finally {
      busy = null;
    }
  }

  async function runTest(w: Webhook): Promise<void> {
    busy = w.key;
    try {
      const result = await testWebhook(skoolId, w.key);
      lastTest = { ...lastTest, [w.key]: result };
      onflash(result.ok
        ? `Delivered — your receiver answered ${result.status_code}.`
        : deliveryReason(result.error) || 'Nothing came back.',
        result.ok ? 'info' : 'error');
      await load();
    } catch (e) {
      // A REFUSAL CARRIES ITS REASON, and that reason is the vocabulary the log already
      // speaks — so a test that was refused reads the same way there as here, rather
      // than as whatever sentence the server happened to wrap around it.
      const refused = isCodedError(e) ? e.details['reason'] : null;
      onflash(
        typeof refused === 'string'
          ? deliveryReason(refused)
          : e instanceof Error ? e.message : 'The test could not be sent.',
        'error');
    } finally {
      busy = null;
    }
  }

  async function showDeliveries(w: Webhook): Promise<void> {
    if (deliveries[w.key]) {
      const { [w.key]: _gone, ...rest } = deliveries;
      deliveries = rest;
      return;
    }
    try {
      const data = await fetchDeliveries(skoolId, w.key);
      deliveries = { ...deliveries, [w.key]: data.items };
    } catch (e) {
      onflash(e instanceof Error ? e.message : 'Could not load deliveries.', 'error');
    }
  }

  function draftFor(key: string): { event: string; roles: string[] } {
    return drafts[key] ?? { event: ROLE_CHANGED_EVENT, roles: [] };
  }

  function setDraft(key: string, patch: Partial<{ event: string; roles: string[] }>): void {
    drafts = { ...drafts, [key]: { ...draftFor(key), ...patch } };
  }

  function toggleRole(key: string, role: string): void {
    const current = draftFor(key).roles;
    setDraft(key, {
      roles: current.includes(role) ? current.filter((r) => r !== role) : [...current, role],
    });
  }

  async function wire(w: Webhook): Promise<void> {
    const draft = draftFor(w.key);
    busy = w.key;
    try {
      await addTrigger(skoolId, w.key, {
        event: draft.event,
        condition: draft.event === ROLE_CHANGED_EVENT ? roleCondition(draft.roles) : null,
      });
      const { [w.key]: _gone, ...rest } = drafts;
      drafts = rest;
      await load();
      onflash('Trigger added — turn it on when you are ready.');
    } catch (e) {
      onflash(e instanceof Error ? e.message : 'Could not add that trigger.', 'error');
    } finally {
      busy = null;
    }
  }

  async function toggleTrigger(key: string, on: boolean): Promise<void> {
    busy = key;
    try {
      await setTriggerEnabled(skoolId, key, on);
      await load();
    } catch (e) {
      onflash(e instanceof Error ? e.message : 'Could not change that.', 'error');
    } finally {
      busy = null;
    }
  }

  async function dropTrigger(key: string): Promise<void> {
    busy = key;
    try {
      await deleteTrigger(skoolId, key);
      await load();
    } catch (e) {
      onflash(e instanceof Error ? e.message : 'Could not remove that.', 'error');
    } finally {
      busy = null;
    }
  }

  function copy(text: string): void {
    navigator.clipboard?.writeText(text).then(
      () => onflash('Copied.'),
      () => onflash('Could not copy — select it and copy by hand.', 'error'),
    );
  }

  const roleLabel = (r: string): string => QUAD[r as Quadrant]?.label ?? r;
</script>

<p class="text-sm text-base-content/60 -mt-2 mb-5 max-w-2xl">
  Send your members to another system the moment something changes — your CRM, a Zap, an
  n8n flow. Each webhook is one address you own; point as many triggers at it as you like.
  Every delivery is signed, so your receiver can prove it came from us.
</p>

{#if loading}
  <StateBlock state="loading" />
{:else if error}
  <StateBlock state="error" message={error} onretry={load} />
{:else}
  {#if secret}
    <div class="alert alert-success mb-5 flex-col items-start gap-2 text-sm">
      <span>
        <strong>Copy your signing secret now.</strong> This is the only time it is shown —
        we keep it encrypted and there is no way to read it back. Your receiver uses it to
        verify the <code>Sideguide-Signature</code> header.
      </span>
      <div class="flex gap-2 w-full items-center">
        <!-- privacy-ok: a generated signing secret for the operator's own endpoint, not member data -->
        <code class="flex-1 bg-base-100 rounded px-2 py-1 text-xs break-all">{secret.value}</code>
        <button class="btn btn-sm" onclick={() => secret && copy(secret.value)}>Copy</button>
        <button class="btn btn-ghost btn-sm" onclick={() => (secret = null)}>Done</button>
      </div>
    </div>
  {/if}

  {#if sample}
    <details class="collapse collapse-arrow bg-base-100 ring-1 ring-base-content/10 mb-5">
      <summary class="collapse-title text-sm font-medium">
        What we send, and how to receive it
      </summary>
      <div class="collapse-content text-sm flex flex-col gap-3">
        <p class="text-base-content/60">
          Every delivery is a <code>POST</code> with this JSON body. <code>data</code> is
          flat — every key is always there, and any of them can be null.
        </p>
        <div class="overflow-x-auto">
          <!-- privacy-ok: a synthetic example ("Jordan Rivera"), never a real member -->
          <pre class="text-xs bg-base-200 rounded p-3 max-h-80 overflow-y-auto"><code
            >{JSON.stringify(sample, null, 2)}</code></pre>
        </div>
        {#if signature}
          <p class="text-base-content/60">
            Each request carries <code>{signature.header}</code>, formatted
            <code>{signature.format}</code> — {signature.algorithm} over
            {signature.signed_material}, keyed with the signing secret you copied when you
            added the webhook. Verify it before parsing, and reject anything more than five
            minutes old.
          </p>
        {/if}
        {#if signature?.delivery_header}
          <p class="text-base-content/60">
            Each request also carries <code>{signature.delivery_header}</code>, shaped
            <code>{signature.delivery_format}</code>. {signature.delivery_note}
          </p>
        {/if}
        <div class="flex items-center gap-2 flex-wrap">
          <button class="btn btn-sm btn-primary"
                  onclick={() => copy(aiPrompt(sample, signature))}>
            Copy AI prompt
          </button>
          <span class="text-xs text-base-content/50">
            Paste it into ChatGPT or Claude and it will write the handler — the body shape,
            the signature check and the retry rules are all in there.
          </span>
        </div>
      </div>
    </details>
  {/if}

  {#if !form}
    <div class="mb-5">
      <button class="btn btn-primary btn-sm" onclick={() => (form = { name: '', url: '' })}>
        + Add a webhook
      </button>
    </div>
  {:else}
    <div class="card bg-base-100 ring-1 ring-primary/30 mb-6 shadow-sm">
      <div class="card-body gap-4">
        <h3 class="font-semibold text-base">New webhook</h3>
        <div class="grid gap-4 sm:grid-cols-2">
          <label class="form-control">
            <span class="label-text text-xs font-medium text-base-content/60 mb-1">Name</span>
            <!-- privacy-ok: the operator naming their own webhook, not data we hold -->
            <input class="input input-bordered input-sm" bind:value={form.name}
                   placeholder="My CRM" />
          </label>
          <label class="form-control">
            <span class="label-text text-xs font-medium text-base-content/60 mb-1">
              URL to call
            </span>
            <!-- privacy-ok: the operator's own endpoint address, not a person -->
            <input class="input input-bordered input-sm" bind:value={form.url}
                   placeholder="https://hooks.example.com/sideguide" />
          </label>
        </div>
        <p class="text-xs text-base-content/50">
          Must start with <code>https://</code>. We will not call a private or local
          address — a webhook pointed inside your own network is a security hole, not a
          feature.
        </p>
        <div class="flex gap-2 justify-end">
          <button class="btn btn-ghost btn-sm" onclick={() => (form = null)}>Cancel</button>
          <button class="btn btn-primary btn-sm" disabled={!canSave || busy === 'create'}
                  onclick={save}>
            {busy === 'create' ? 'Adding…' : 'Add webhook'}
          </button>
        </div>
      </div>
    </div>
  {/if}

  {#if items.length === 0}
    <StateBlock state="empty" message="No webhooks yet." />
  {:else}
    <div class="flex flex-col gap-4">
      {#each items as w (w.key)}
        <!-- Indexed lookups into a Record are `T | undefined`, and `{@const}` may only sit
             as an immediate child of a block — so both are resolved once, here, and
             narrowed with `{#if}` where used. `tested`, not `t`: the trigger loop below
             binds `t`. -->
        {@const tested = lastTest[w.key]}
        {@const draft = draftFor(w.key)}
        {@const log = deliveries[w.key]}
        {@const state = endpointCopy(w)}
        <!-- THE LAST ATTEMPT, IN THE LOG'S OWN WORDS. `parked_reason` says why we
             stopped, which the state sentence below already covers; what the owner
             cannot get anywhere else without opening the log is what actually came
             back. Blank when it IS the blocking state, which says it once already. -->
        {@const lastReason = w.last_error && w.last_error !== 'refused_no_secret'
          ? deliveryReason(w.last_error) : ''}
        <div class="card bg-base-100 ring-1 ring-base-content/10">
          <div class="card-body gap-4 py-5">
            <!-- identity, health, and the on/off switch -->
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <!-- privacy-ok: the operator's own webhook name -->
                  <h3 class="font-semibold truncate">{w.name}</h3>
                  {#if !w.enabled}
                    <span class="badge badge-ghost badge-sm">Off</span>
                  {/if}
                  {#if w.last_status !== null}
                    <span class="badge badge-sm
                                 {deliveryHealth(w.last_status) === 'ok'
                                   ? 'badge-success' : 'badge-error'}">
                      {w.last_status}
                    </span>
                  {/if}
                  <!-- PARKED IS OURS, FAILING IS THEIRS, and `enabled: false` alone
                       cannot tell an owner which — so the badge says so. The sentence
                       under the card is what says what to do about it. -->
                  {#if state}
                    <span class="badge badge-sm
                                 {state.label === 'Cannot send' ? 'badge-error' : 'badge-warning'}">
                      {state.label}
                    </span>
                  {/if}
                </div>
                <!-- MARKED, not excused. A Zapier or Make catch hook carries its secret
                     in the path — the backend refuses to log this value for that exact
                     reason — so it is the last thing that should stay legible on a
                     recording of somebody's screen. -->
                <p class="text-xs text-base-content/50 mt-0.5 break-all redact">{w.url}</p>
                <p class="text-xs text-base-content/40 mt-0.5">
                  {#if w.last_delivery_at}Last delivery {timeAgo(w.last_delivery_at)}{/if}
                  <!-- THE AGE `last_status` CANNOT CARRY. One integer with no clock
                       beside it reads the same whether the receiver accepted something a
                       minute ago or last Tuesday, and that is the whole question. -->
                  {#if w.health?.last_ok_at}
                    · last accepted {timeAgo(w.health.last_ok_at)}
                  {:else if w.health}
                    · never accepted by this receiver
                  {/if}
                </p>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <button class="btn btn-ghost btn-xs" disabled={busy === w.key}
                        onclick={() => runTest(w)}>Send test</button>
                <button class="btn btn-ghost btn-xs"
                        onclick={() => showDeliveries(w)}>
                  {log ? 'Hide log' : 'Log'}
                </button>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" class="toggle toggle-sm" checked={w.enabled}
                         disabled={busy === w.key} onchange={() => toggle(w)} />
                </label>
                <button class="btn btn-ghost btn-xs text-error"
                        onclick={() => remove(w)}>Delete</button>
              </div>
            </div>

            <!-- THE STATE, IN WORDS, WITH THE ONE THING TO DO ABOUT IT. A badge can say
                 "parked"; only a sentence can say that we stopped calling after a day of
                 refusals and that turning it back on is what resumes it. -->
            {#if state}
              <div class="text-xs rounded px-3 py-2 flex flex-col gap-1
                          {state.label === 'Cannot send'
                            ? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'}">
                <p>{state.what}</p>
                {#if lastReason || w.health?.failing_for_secs || w.parked_at}
                  <p class="opacity-80">
                    {#if lastReason}Last attempt: {lastReason}{/if}
                    {#if w.health?.failing_for_secs}
                      Failing for {ageWords(w.health.failing_for_secs)}.
                    {/if}
                    {#if w.parked_at}Paused {timeAgo(w.parked_at)}.{/if}
                  </p>
                {/if}
                <div class="flex items-center gap-2 flex-wrap">
                  <span>{state.fix}</span>
                  {#if w.health?.status === 'parked'}
                    <!-- The SAME enable route, named for what pressing it does here:
                         clearing a park and switching an endpoint back on are one
                         action on the server, and two different things to a reader. -->
                    <button class="btn btn-xs btn-warning" disabled={busy === w.key}
                            onclick={() => toggle(w)}>
                      Re-enable
                    </button>
                  {/if}
                </div>
              </div>
            {/if}

            {#if tested}
              <div class="text-xs rounded px-3 py-2
                          {tested.ok ? 'bg-success/10' : 'bg-error/10'}">
                {#if tested.ok}
                  Delivered in {tested.duration_ms}ms — your receiver answered
                  {tested.status_code}.
                {:else}
                  {deliveryReason(tested.error) || 'Nothing came back.'}
                {/if}
                {#if tested.sample_member}
                  <!-- privacy-ok: wrapped in .redact so private mode blurs the member's name -->
                  <span class="redact">We described {tested.sample_member}.</span>
                {/if}
              </div>
            {/if}

            <!-- ---------------------------- triggers ---------------------------- -->
            <div class="border-t border-base-content/10 pt-3">
              <p class="text-xs font-medium text-base-content/60 mb-2">Fires when</p>
              {#if w.triggers.length === 0}
                <p class="text-xs text-base-content/40 mb-2">
                  Nothing yet — this webhook will never fire until you add a trigger.
                </p>
              {:else}
                <div class="flex flex-col gap-1.5 mb-3">
                  {#each w.triggers as t (t.key)}
                    <div class="flex items-center gap-2 text-sm">
                      <input type="checkbox" class="toggle toggle-xs" checked={t.enabled}
                             disabled={busy === t.key}
                             onchange={() => toggleTrigger(t.key, !t.enabled)} />
                      <span>{t.event_label}</span>
                      {#if t.condition}
                        <span class="badge badge-ghost badge-xs">
                          only {((t.condition['value'] as string[]) ?? [])
                                  .map(roleLabel).join(', ')}
                        </span>
                      {/if}
                      {#if t.enabled && t.enabled_at}
                        <span class="text-xs text-base-content/40">
                          on since {timeAgo(t.enabled_at)}
                        </span>
                      {/if}
                      <span class="flex-1"></span>
                      <button class="btn btn-ghost btn-xs text-error"
                              onclick={() => dropTrigger(t.key)}>Remove</button>
                    </div>
                  {/each}
                </div>
              {/if}

              <div class="flex flex-wrap items-end gap-2">
                <label class="form-control">
                  <span class="label-text text-xs text-base-content/50 mb-1">Add a trigger</span>
                  <select class="select select-bordered select-xs"
                          value={draft.event}
                          onchange={(e) => setDraft(w.key, {
                            event: (e.currentTarget as HTMLSelectElement).value })}>
                    <!-- privacy-ok: ev.name is the EVENT id ("member.role_changed"), not a person -->
                    {#each events as ev}<option value={ev.name}>{ev.label}</option>{/each}
                  </select>
                </label>
                <button class="btn btn-sm" disabled={busy === w.key}
                        onclick={() => wire(w)}>Add</button>
              </div>

              {#if draft.event === ROLE_CHANGED_EVENT}
                <div class="mt-2">
                  <p class="text-xs text-base-content/50 mb-1">
                    Only when they become… <span class="text-base-content/40">
                      (leave empty for any role change)</span>
                  </p>
                  <div class="flex flex-wrap gap-1.5">
                    {#each ROLE_ORDER_ALL as role}
                      <button class="badge badge-sm cursor-pointer
                                     {draft.roles.includes(role)
                                       ? 'badge-primary' : 'badge-ghost'}"
                              onclick={() => toggleRole(w.key, role)}>
                        {QUAD[role].label}
                      </button>
                    {/each}
                  </div>
                  <p class="text-xs text-base-content/40 mt-1.5">
                    The body carries where they came from too, so you can tell a promotion
                    from a drop.
                  </p>
                </div>
              {/if}
            </div>

            <!-- ---------------------------- the log ----------------------------- -->
            {#if log}
              <div class="border-t border-base-content/10 pt-3">
                {#if log.length === 0}
                  <p class="text-xs text-base-content/40">Nothing delivered yet.</p>
                {:else}
                  <p class="text-xs text-base-content/40 mb-2">
                    Every attempt, newest first. A retry is its own line — the delivery id
                    is what your receiver saw in
                    <code>{signature?.delivery_header ?? 'Sideguide-Delivery'}</code>.
                  </p>
                  <div class="overflow-x-auto">
                    <table class="table table-xs">
                      <tbody>
                        {#each log as d (d.id)}
                          <tr>
                            <!-- privacy-ok: the EVENT id ("member.joined"), not a person -->
                            <td class="text-base-content/60 align-top">{d.event}</td>
                            <td class="align-top">
                              <!-- `0` NEVER READS AS A STATUS. It is the server's way of
                                   saying the attempt never became a request, and showing
                                   the digit invites "my server returned 0", which it
                                   did not. -->
                              <span class="badge badge-xs whitespace-nowrap
                                           {deliveryHealth(d.status_code) === 'ok'
                                             ? 'badge-success' : 'badge-error'}">
                                {d.status_code === 0
                                  ? 'not sent'
                                  : d.status_code ?? 'no answer'}
                              </span>
                            </td>
                            <td class="text-base-content/40 align-top whitespace-nowrap">
                              {d.duration_ms ?? '—'}{d.duration_ms == null ? '' : 'ms'}
                            </td>
                            <td class="text-base-content/40 align-top whitespace-nowrap">
                              {d.created_at ? timeAgo(d.created_at) : ''}
                            </td>
                            <td class="text-base-content/40 align-top whitespace-nowrap">
                              {#if d.attempt}try {d.attempt}{/if}
                            </td>
                            <td class="text-base-content/40 align-top font-mono text-[0.68rem]">
                              <!-- privacy-ok: an opaque delivery id ("918.2"), the one
                                   handle this end and the receiver share -->
                              {d.delivery_id ?? ''}
                            </td>
                            <td class="text-xs align-top
                                       {deliveryHealth(d.status_code) === 'ok'
                                         ? 'text-base-content/40' : 'text-error'}">
                              {deliveryReason(d.error)}
                            </td>
                          </tr>
                        {/each}
                      </tbody>
                    </table>
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        </div>
      {/each}
    </div>
  {/if}
{/if}
